import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { Label, SelectInternal, Wrapper } from "./styles";
import { Select } from "antd";

// Normaliza texto para a busca: remove acentos e ignora maiúsculas/minúsculas.
const normalizeSearchText = (value) =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

// Limites (em ch) da largura dinamica do campo pesquisavel.
const MIN_CONTENT_CH = 6;
const MAX_CONTENT_CH = 40;

const SelectWithInternalLabel = ({
  label,
  options,
  value,
  onChange,
  searchable = false,
  defaultOption2 = false,
}) => {
  const intl = useIntl();
  const allOpt = intl.formatMessage({ id: "all" });
  const allOpt2 = intl.formatMessage({ id: "all2" });

  const { Option } = Select;

  const [searchText, setSearchText] = useState("");

  const selectedLabel = useMemo(() => {
    const selected = options.find(
      (option) => String(option.value) === String(value)
    );
    if (selected) return String(selected.label ?? "");
    return defaultOption2 ? allOpt2 : allOpt;
  }, [options, value, allOpt, allOpt2, defaultOption2]);

  const displayText = searchText || selectedLabel;

  const dynamicWidth = useMemo(() => {
    if (!searchable) return undefined;
    const contentCh = Math.min(
      Math.max(displayText.length + 3, MIN_CONTENT_CH),
      MAX_CONTENT_CH
    );
    return `calc(${contentCh}ch + 24px)`;
  }, [searchable, displayText]);

  const handleChange = (selectedValue, option) => {
    setSearchText("");
    onChange(selectedValue, option);
  };

  return (
    <Wrapper>
      <Label>{label}</Label>
      <SelectInternal
        bordered={false}
        value={value || "all"}
        onChange={handleChange}
        listHeight="auto"
        showSearch={searchable}
        onSearch={searchable ? setSearchText : undefined}
        onDropdownVisibleChange={
          searchable
            ? (open) => {
                if (!open) setSearchText("");
              }
            : undefined
        }
        style={dynamicWidth ? { width: dynamicWidth } : undefined}
        filterOption={
          searchable
            ? (input, option) =>
                normalizeSearchText(option?.children).includes(
                  normalizeSearchText(input)
                )
            : undefined
        }
        virtual={false}
      >
        {!options.some((option) => option.value === "all") && (
          <Option value="all">{defaultOption2 ? allOpt2 : allOpt}</Option>
        )}

        {options.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </SelectInternal>
    </Wrapper>
  );
};

SelectWithInternalLabel.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  searchable: PropTypes.bool,
  defaultOption2: PropTypes.bool,
};

export default SelectWithInternalLabel;
